import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  SurveysSettingsAuthorsDocument,
  SurveysSettingsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import AddSurveyQuestionnaireTemplateModal from 'modules/surveys/components/QuestionTemplates/AddSurveyQuestionnaireTemplateModal'
import Table from 'modules/surveys/pages/QuestionsTemplates/QuestionsTemplates/Table'
import questionsTemplates from 'modules/surveys/store/collections/questionsTemplates'

export type AuthorsMap = Map<string, { firstName: string; lastName: string }>

const SurveySettings = () => {
  const [isModalOpen, toggleModal] = React.useState<boolean>(false)
  useAsyncData(() => questionsTemplates.fetch(), [])

  const questionsTemplatesList = questionsTemplates.models.filter(
    (questionsTemplate) => questionsTemplate.isSurvey
  )

  const accessIds = React.useMemo(
    () => [...new Set(questionsTemplatesList.map((t) => t.attributes.access_id))].filter(Boolean),
    [questionsTemplatesList]
  )

  const { data, loading } = useQuery(SurveysSettingsDocument, {})
  const { data: authorsData } = useQuery(SurveysSettingsAuthorsDocument, {
    variables: { accessIds },
    skip: accessIds.length === 0,
  })

  const authorsMap: AuthorsMap = React.useMemo(() => {
    const map: AuthorsMap = new Map()
    authorsData?.apiCore.accesses.forEach((access) => {
      map.set(access.id, { firstName: access.firstName, lastName: access.lastName })
    })
    return map
  }, [authorsData])

  const canCreateSurveyTemplates = data?.surveys._policies.createSurveyTemplates.can ?? false

  if (loading) {
    return <Loading />
  }

  if (!canCreateSurveyTemplates) {
    return <NotFound />
  }

  return (
    <ResponsivePage.Body>
      <Box alignItems="flexEnd" gap="s24">
        <Button onClick={() => toggleModal(true)} iconName="add">
          {i18n.t('app_store_v2.apps.performance_v2.settings.templates.add')}
        </Button>
        <Table questionsTemplatesList={questionsTemplatesList} authorsMap={authorsMap} />
      </Box>
      {isModalOpen && <AddSurveyQuestionnaireTemplateModal toggleModal={toggleModal} />}
    </ResponsivePage.Body>
  )
}

export default observer(SurveySettings)
